
import initRouter from '../router'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { CustomButton, HeaderTitle, Navigation, Spinner } from 'ui-library'
import i18n from '@/i18n'
import { Route } from 'vue-router'
import ScriptLoader from '@/utils/ScriptLoader'
import RedCooperationUtils from '@/utilsTenant/RedCooperationUtils'
import PersonalDataIconBox from '@/componentsTenant/container/PersonalDataIconBox.vue'
import SettingsIconBox from '@/componentsTenant/container/SettingsIconBox.vue'
import VertragsServicesIconBox from '@/componentsTenant/container/VertragsServicesIconBox.vue'
import TeaserWelcome from '@/componentsTenant/TeaserWelcome.vue'
import ThankYouPage from '@/pages/ThankYouPage.vue'
import { trkClick, updateTrackingInformation } from '@/utilsTenant/tracking'
import { WebTrekkMouseEvent } from '@/webtrekk'
import { getPartnerProxyId } from '@/utils/KeycloakUtils'
import { menuItems as menuItemsTenant } from '@/menuItems'
import { getQueryParameterKey } from '@/utils/utils'

initRouter()

interface MenuItem {
  name: string
  path?: string
  icon?: string
}

@Component({
  i18n,
  components: {
    SettingsIconBox,
    PersonalDataIconBox,
    VertragsServicesIconBox,
    Navigation,
    HeaderTitle,
    CustomButton,
    TeaserWelcome,
    Spinner,
    ThankYouPage
  }
})
export default class CustomerPortal extends Vue {
  get calculatedTopPos(): number {
    return this.topPosition + this.offsetForNavigation
  }

  public offsetForNavigation = 0
  public topPosition = 0
  public positionStyle: string | null = null
  public parentHasStickyHeader = false
  public resizeTimeout: ReturnType<typeof setTimeout> | null = null
  public scrollPosBeforeScroll = 0
  public paddingForHeadline: number | string = 0
  private menuItems: MenuItem[] = []
  private activeMenuItem: MenuItem | undefined
  private headline = ''
  private subline = ''
  private isPublicPage = true
  private partnerProxyId: string | null = null
  private assetsBaseUrl: string = window.ENV_CONFIG.ASSETS_BASE_URL.CUSTOMER_PORTAL || ''
  private loading = true

  private trackingTimeout: ReturnType<typeof setTimeout> | undefined | null

  @Prop({ default: false, type: Boolean })
  private showNoClaimsData!: boolean

  @Prop({ default: false, type: Boolean })
  private showModificationDate!: boolean

  private initPartnerProxyId(): void {
    this.partnerProxyId = getPartnerProxyId()
  }

  public created(): void {
    this.initPartnerProxyId()

    this.menuItems = menuItemsTenant

    // initial app loading
    this.$router.onReady(async () => {
      const andIgnore = () => undefined
      // Postboxtile and Partner Greeting used in Head, needs to be preloaded
      await Promise.all([
        ScriptLoader.preloadScript(window.ENV_CONFIG.WEB_COMPONENTS.POSTBOX).catch(andIgnore),
        ScriptLoader.preloadScript(window.ENV_CONFIG.WEB_COMPONENTS.PARTNER_PROXY).catch(andIgnore)
      ])
      this.updateNavigationState(this.$router.currentRoute)
      updateTrackingInformation(this.$router.currentRoute)
    })

    // every route change
    this.$router.beforeEach((to: Route, from: Route, next: () => void) => {
      this.updateNavigationState(to)
      this.trackingTimeout = setTimeout(() => {
        updateTrackingInformation(to)
      }, 0)
      next()
    })

    window.addEventListener('scroll', this.handleScroll)
    const body = document.body
    if (body) {
      body.addEventListener('scroll', this.handleScroll)
    }
    window.addEventListener('resize', this.handleResizeDelayed)
  }

  public destroyed(): void {
    window.removeEventListener('scroll', this.handleScroll)
    const body = document.body
    if (body) {
      body.removeEventListener('scroll', this.handleScroll)
    }
    window.removeEventListener('resize', this.handleResizeDelayed)
    if (this.trackingTimeout) {
      clearTimeout(this.trackingTimeout)
    }
  }

  public handleScroll(e: Event): void {
    // do this only when needed, in other cases navigation should have style position:sticky (default)
    if (this.parentHasStickyHeader) {
      const scrollPosCurrent = e.target === document.body ? document.body.scrollTop : window.pageYOffset
      const scrollDiff = this.scrollPosBeforeScroll - scrollPosCurrent

      // show navigation, if scroll position is 0, or user scrolles up;
      // hide navigaton when user scrolls down

      // show navigation
      if (scrollPosCurrent <= 0 || scrollDiff >= 0) {
        this.topPosition = 0
      } else if (scrollDiff < 0) {
        // hide navigation
        /*
            -500: ~more than 2 * max-height of sub nav
            adjust this, if this.menuItems will get some more entries
        */
        this.topPosition = -500
      }
      this.scrollPosBeforeScroll = scrollPosCurrent
    }
  }

  public checkStateOfParentHeaders(): void {
    /* check this stuff; pass in selectors as props? */
    const header = document.querySelector('#header') as HTMLElement
    const mobileHeader = document.querySelector('#header .mobile-head') as HTMLElement
    /*check if mobile header exits, is visible*/
    if (
      header &&
      mobileHeader &&
      (getComputedStyle(mobileHeader).display !== 'none' || getComputedStyle(header).position === 'fixed')
    ) {
      this.parentHasStickyHeader = true
      const mobileHeaderHeight = mobileHeader.offsetHeight
      const headerHeight = header.offsetHeight
      /* get maximum offset for navigation */
      this.offsetForNavigation = Math.max(mobileHeaderHeight, headerHeight)
      /* set offset for headline only if nescessary */
      this.paddingForHeadline = headerHeight > 0 ? 0 : `${mobileHeaderHeight}px`
    } else {
      this.parentHasStickyHeader = false
      this.offsetForNavigation = 0
      this.paddingForHeadline = 0
    }
    this.positionStyle = this.parentHasStickyHeader ? 'fixed' : null
  }

  public handleResizeDelayed(): void {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
    // avoid performance issues while resizing
    this.resizeTimeout = setTimeout(() => {
      this.checkStateOfParentHeaders()
    }, 25)
  }

  public mounted(): void {
    // The Document type does not define the property documentMode. This property is only set on IE11, therefore
    // a cast to any is required in order to access the property without getting typescript errors.
    // See https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window.isIE11 = () => false || !!(document as any).documentMode
    // eslint-disable-next-line no-undef
    window.addEventListener('appNavigation', this.onAppNavigation as EventListener)
    window.newNavigationEvent = this.newNavigationEvent
    this.checkStateOfParentHeaders()

    window.dispatchEvent(
      new CustomEvent('cp_loaded_successful', {
        bubbles: true,
        detail: {}
      })
    )
    setTimeout(() => {
      this.loading = false
    }, 1000)
  }

  public clickNaviLink(event: Event): void {
    this.$router.push({ path: (event.target as HTMLInputElement).dataset.path })
  }

  public trkClickMethod(e: WebTrekkMouseEvent): void {
    trkClick(e)
  }

  private updateNavigationState(currentRoute: Route) {
    this.activeMenuItem = this.menuItems.find(
      (menuItem: MenuItem) => menuItem.path && currentRoute.fullPath.indexOf(menuItem.path) !== -1
    )
    // use /vertraege MenuItem for /abschluss w/o adding another Tab in the navigation bar
    if (currentRoute.fullPath.indexOf('/abschluss') !== -1)
      this.activeMenuItem = this.menuItems.find((menuItem: MenuItem) => menuItem.path && menuItem.path == '/vertraege')

    // use /persoenliche-daten MenuItem for /danke w/o adding another Tab in the navigation bar
    if (currentRoute.fullPath.indexOf('/danke') !== -1)
      this.activeMenuItem = this.menuItems.find(
        (menuItem: MenuItem) => menuItem.path && menuItem.path == '/persoenliche-daten'
      )

    if (!this.activeMenuItem) {
      this.isPublicPage = true // we are on a page which is not in the navigation menu => public page
      this.activeMenuItem = this.menuItems[0] // init with first menu item element
    } else {
      this.isPublicPage = false // we are on a page which is in the navigation menu => secured page
    }

    this.headline = String(this.$t('headline.' + currentRoute.name))
    this.subline = String(this.$t('subline.' + currentRoute.name))

    if (currentRoute.fullPath.indexOf('/danke') !== -1) {
      const vorgang = getQueryParameterKey()
      this.headline = String(this.$t('headline.' + vorgang))
      this.subline = String(this.$t('subline.' + vorgang))
    }
  }

  private onAppNavigation(event: CustomEvent<{ url: string }>) {
    // Sitecore cannot detect the login state when a router push is used after a successful login
    // Absolute URLs should not be navigated to by a router push as well
    /* TODO this might not be an ideal solution once deeplinks have to be supported.
        For deeplinks a further differentiation between source and target URL might be helpful and should be added to
        the event.
    */
    /* TODO: Check for origin === Login Page (/anmelden) when navigate to Overview Page (/ueberblick)
            to prevent full page refresh on other origins than Login Page (/anmelden)
            Code: && window.location.href.includes(window.ENV_CONFIG.REDIRECTS.LOGIN_PAGE))
            Tests have to be modified for this.
   */
    if (event.detail.url.startsWith('http')) {
      if (event.detail.url === window.ENV_CONFIG.REDIRECTS.ACCOUNT_CREATION_SUCCESS_PAGE) {
        window.open(event.detail.url, '_self')
      } else {
        window.open(event.detail.url, '_blank')
      }
    } else if (event.detail.url.startsWith('tel:')) {
      window.open(event.detail.url, '_self')
    } else if (
      // Links without router handling
      // Relative paths for potentially not logged-in users
      event.detail.url === window.ENV_CONFIG.REDIRECTS.LOGIN_PAGE ||
      event.detail.url === window.ENV_CONFIG.REDIRECTS.ACCOUNT_LOGIN_ON_SUCCESS_REDIRECT ||
      event.detail.url === window.ENV_CONFIG.REDIRECTS.PERSONAL_DATA_CHANGE_ADDRESS_PAGE
    ) {
      // if a string instead of a RegEx is used, eg. ('//', '/'), replace will only replace the first occurrence
      const url: string = (window.ENV_CONFIG.BASE_URL + event.detail.url).replace(new RegExp('//', 'g'), '/')
      window.location.assign(url)
    } else {
      this.$router.push(event.detail.url)
    }
  }

  private newNavigationEvent(url: string): void {
    try {
      if (window.isIE11()) {
        const params = { bubbles: true, cancelable: false, detail: { url } }
        const evt = document.createEvent('CustomEvent')
        evt.initCustomEvent('appNavigation', params.bubbles, params.cancelable, params.detail)
        window.dispatchEvent(evt)
      } else {
        window.dispatchEvent(
          new CustomEvent('appNavigation', {
            bubbles: true,
            detail: {
              url
            }
          })
        )
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
      window.location.assign(url)
    }
  }

  get isRedCooperationPartner(): boolean {
    return RedCooperationUtils.isRedCooperationPartner()
  }

  get showSubline(): boolean {
    const routeNames: Array<string | undefined> = [
      'email',
      'policen',
      'quotes',
      'fahrzeugWechseln',
      'coronaBeitragserstattung',
      'einstellungen',
      'personalData',
      'changeAddress',
      'changeName',
      'changeBankData',
      'vertragsServices',
      'motorCockpitFahrleistung',
      'motorCockpitVersicherungsumfang',
      'motorCockpitFahrerkreis',
      'schaeden',
      'preBeitragsbescheinigung',
      'requestBeitragsbescheinigung',
      'danke'
    ]
    return this.$route.name ? routeNames.includes(this.$route.name) : false
  }

  get isHeaderColorable(): boolean {
    const routes = [window.ENV_CONFIG.REDIRECTS.ACCOUNT_LOGIN_ON_SUCCESS_REDIRECT]
    return routes.includes(this.$route.path)
  }

  get isBodyBackground(): boolean {
    const routes = [window.ENV_CONFIG.REDIRECTS.LOGIN_PAGE, window.ENV_CONFIG.REDIRECTS.ACCOUNT_REGISTRATION_PAGE]
    return !routes.includes(this.$route.path)
  }
}
